import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import {
  onSdkIncident,
  registerHost,
  type OrderformState as SdkState,
  type OrderformStoreRead,
  type SdkIncident,
} from '@ds24/orderform-sdk';
import { getOrderformState } from '../ngrx/orderform.selectors';
import { MonitoringService } from '../services/monitoring.service';

/**
 * Feeds the shared SDK's read-only store from the Angular NgRx store and reports singleton
 * incidents (another bundle carrying a different SDK version) to APM, where the browser
 * console is not watched.
 */
@Injectable({ providedIn: 'root' })
export class OrderformSdkHostService {
  constructor(private readonly _store: Store, private readonly _monitoring: MonitoringService) {}

  init(): void {
    registerHost({ store: this._buildStore() });
    onSdkIncident((incident) => this._monitoring.captureError(new Error(describeIncident(incident))));
  }

  private _buildStore(): OrderformStoreRead {
    const snapshot = (): SdkState => {
      let state!: SdkState;
      this._store
        .select(getOrderformState)
        .pipe(take(1))
        .subscribe((s) => (state = s as unknown as SdkState));
      return state;
    };
    return {
      getState: snapshot,
      select: (selector) => selector(snapshot()),
      select$: (selector) => ({
        subscribe: (next, error) => {
          const sub = this._store
            .select(getOrderformState)
            .pipe(
              map((s) => selector(s as unknown as SdkState)),
              distinctUntilChanged()
            )
            .subscribe({ next, error });
          return { unsubscribe: () => sub.unsubscribe() };
        },
      }),
      subscribe: (listener) => {
        const sub = this._store.select(getOrderformState).subscribe((s) => listener(s as unknown as SdkState));
        return { unsubscribe: () => sub.unsubscribe() };
      },
    };
  }
}

function describeIncident(incident: SdkIncident): string {
  return `[orderform-sdk] ${incident.kind}: page had ${incident.incumbent ?? 'an unversioned copy'}, a bundle with ${
    incident.newcomer
  } arrived`;
}
